import { useQuery } from "@tanstack/react-query";
import { Download } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
  CartesianGrid,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  downloadTipoCambioCsv,
  getTipoCambioCatalogo,
  getTipoCambioSerie,
} from "@/lib/api";

const STORAGE_KEY = "portal-edocta:tipo-cambio-config";
const PAGE_SIZE = 50;

const VAL_FMT = new Intl.NumberFormat("es-MX", {
  minimumFractionDigits: 4,
  maximumFractionDigits: 4,
});

function fmtValor(v: string | number | null | undefined): string {
  if (v == null) return "—";
  const n = typeof v === "number" ? v : Number(v);
  if (!Number.isFinite(n)) return "—";
  return VAL_FMT.format(n);
}

function fmtFecha(s: string): string {
  return new Date(s + "T00:00:00").toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function fmtFechaCorta(s: string): string {
  return new Date(s + "T00:00:00").toLocaleDateString("es-MX", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
}

type StoredCfg = {
  moneda?: string;
  desde?: string;
  hasta?: string;
};

export function TipoCambioPage() {
  const stored = useMemo<StoredCfg>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const [moneda, setMoneda] = useState<string>(stored.moneda ?? "USD");
  const [desde, setDesde] = useState<string>(stored.desde ?? "");
  const [hasta, setHasta] = useState<string>(stored.hasta ?? "");
  const [page, setPage] = useState(0);
  const [orderDesc, setOrderDesc] = useState(true); // tabla: más reciente primero
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ moneda, desde, hasta }),
      );
    } catch {
      // ignore
    }
  }, [moneda, desde, hasta]);

  const catalogoQ = useQuery({
    queryKey: ["tipo-cambio:catalogo"],
    queryFn: getTipoCambioCatalogo,
    staleTime: 10 * 60_000,
  });

  // Default desde/hasta: mes actual (primer día del mes hasta el último dato
  // disponible, que normalmente es hoy o ayer).
  useEffect(() => {
    if (!catalogoQ.data) return;
    const cur = catalogoQ.data.items.find((m) => m.moneda === moneda);
    if (!cur) return;
    if (!desde) {
      const d = new Date();
      const pad = (n: number) => String(n).padStart(2, "0");
      setDesde(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`);
    }
    if (!hasta && cur.fecha_max) {
      setHasta(cur.fecha_max);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [catalogoQ.data, moneda]);

  const serieQ = useQuery({
    queryKey: ["tipo-cambio:serie", moneda, desde, hasta],
    queryFn: () =>
      getTipoCambioSerie({
        moneda,
        desde: desde || undefined,
        hasta: hasta || undefined,
      }),
    enabled: Boolean(moneda),
  });

  const monedasDisponibles = catalogoQ.data?.items ?? [];
  const monedaInfo = monedasDisponibles.find((m) => m.moneda === moneda);

  const chartData = useMemo(() => {
    if (!serieQ.data) return [];
    return serieQ.data.items
      .filter((r) => r.valor != null)
      .map((r) => ({
        fecha: r.fecha,
        valor: Number(r.valor),
      }))
      .filter((r) => Number.isFinite(r.valor));
  }, [serieQ.data]);

  const tablaData = useMemo(() => {
    if (!serieQ.data) return [];
    const rows = [...serieQ.data.items];
    rows.sort((a, b) =>
      orderDesc ? b.fecha.localeCompare(a.fecha) : a.fecha.localeCompare(b.fecha),
    );
    return rows;
  }, [serieQ.data, orderDesc]);

  const totalPages = Math.ceil(tablaData.length / PAGE_SIZE);
  const tablaPagina = useMemo(
    () => tablaData.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE),
    [tablaData, page],
  );

  useEffect(() => {
    setPage(0);
  }, [moneda, desde, hasta, orderDesc]);

  // Stats rápidos del rango actual
  const stats = useMemo(() => {
    if (chartData.length === 0) return null;
    const valores = chartData.map((r) => r.valor);
    const min = Math.min(...valores);
    const max = Math.max(...valores);
    const ultimo = chartData[chartData.length - 1];
    return { min, max, ultimo };
  }, [chartData]);

  const handleDownload = async () => {
    setDownloading(true);
    try {
      await downloadTipoCambioCsv({
        moneda,
        desde: desde || undefined,
        hasta: hasta || undefined,
      });
    } catch (err) {
      alert(`Error al descargar: ${(err as Error).message}`);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="space-y-4">
      <div className="bg-white border rounded-lg p-4 space-y-3">
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          <Field label="Moneda">
            <select
              className="border rounded px-2 py-1.5 w-full"
              value={moneda}
              onChange={(e) => setMoneda(e.target.value)}
              disabled={catalogoQ.isLoading}
            >
              {monedasDisponibles.length === 0 && (
                <option value="USD">USD</option>
              )}
              {monedasDisponibles.map((m) => (
                <option key={m.moneda} value={m.moneda}>
                  {m.moneda}
                </option>
              ))}
            </select>
          </Field>
          <Field label="Desde">
            <input
              type="date"
              className="border rounded px-2 py-1.5 w-full"
              value={desde}
              min={monedaInfo?.fecha_min ?? undefined}
              max={monedaInfo?.fecha_max ?? undefined}
              onChange={(e) => setDesde(e.target.value)}
            />
          </Field>
          <Field label="Hasta">
            <input
              type="date"
              className="border rounded px-2 py-1.5 w-full"
              value={hasta}
              min={monedaInfo?.fecha_min ?? undefined}
              max={monedaInfo?.fecha_max ?? undefined}
              onChange={(e) => setHasta(e.target.value)}
            />
          </Field>
          <div className="md:col-span-2 flex items-end gap-2 flex-wrap">
            <button
              type="button"
              onClick={() => {
                const d = new Date();
                const pad = (n: number) => String(n).padStart(2, "0");
                setDesde(`${d.getFullYear()}-${pad(d.getMonth() + 1)}-01`);
                if (monedaInfo?.fecha_max) setHasta(monedaInfo.fecha_max);
              }}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Mes actual
            </button>
            <button
              type="button"
              onClick={() => {
                if (!monedaInfo?.fecha_max) return;
                const d = new Date(monedaInfo.fecha_max);
                d.setFullYear(d.getFullYear() - 1);
                setDesde(d.toISOString().slice(0, 10));
                setHasta(monedaInfo.fecha_max);
              }}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Último año
            </button>
            <button
              type="button"
              onClick={() => {
                if (!monedaInfo) return;
                if (monedaInfo.fecha_min) setDesde(monedaInfo.fecha_min);
                if (monedaInfo.fecha_max) setHasta(monedaInfo.fecha_max);
              }}
              className="border rounded px-3 py-1.5 text-sm hover:bg-slate-50"
            >
              Todo el histórico
            </button>
            <button
              type="button"
              onClick={handleDownload}
              disabled={downloading || chartData.length === 0}
              className="inline-flex items-center gap-1 border rounded px-3 py-1.5 text-sm hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Download className="w-4 h-4" />
              {downloading ? "Descargando…" : "CSV"}
            </button>
          </div>
        </div>
        {stats && (
          <div className="pt-2 border-t flex flex-wrap gap-x-6 gap-y-1 text-xs text-slate-600">
            <span>
              Último ({fmtFechaCorta(stats.ultimo.fecha)}):{" "}
              <b className="text-slate-900 tabular-nums">
                ${fmtValor(stats.ultimo.valor)}
              </b>
            </span>
            <span>
              Mín en rango: <b className="tabular-nums">${fmtValor(stats.min)}</b>
            </span>
            <span>
              Máx en rango: <b className="tabular-nums">${fmtValor(stats.max)}</b>
            </span>
            <span>
              {chartData.length.toLocaleString("es-MX")} observaciones
            </span>
          </div>
        )}
      </div>

      <div className="bg-white border rounded-lg p-4">
        <h3 className="text-sm font-semibold text-slate-800 mb-2">
          Tipo de cambio {moneda}/MXN — serie histórica
        </h3>
        {serieQ.isLoading && (
          <div className="p-8 text-center text-slate-500 text-sm">Cargando…</div>
        )}
        {serieQ.isError && (
          <div className="p-8 text-center text-red-600 text-sm">
            Error: {(serieQ.error as Error).message}
          </div>
        )}
        {!serieQ.isLoading && !serieQ.isError && chartData.length === 0 && (
          <div className="p-8 text-center text-slate-500 text-sm">
            Sin datos en el rango seleccionado.
          </div>
        )}
        {chartData.length > 0 && (
          <div className="h-[420px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={chartData}
                margin={{ top: 10, right: 24, left: 0, bottom: 10 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="fecha"
                  tick={{ fontSize: 11, fill: "#475569" }}
                  tickFormatter={fmtFechaCorta}
                  minTickGap={48}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: "#475569" }}
                  tickFormatter={(v) => VAL_FMT.format(Number(v))}
                  width={72}
                  domain={[
                    (dataMin: number) => Math.floor(dataMin * 100) / 100,
                    (dataMax: number) => Math.ceil(dataMax * 100) / 100,
                  ]}
                />
                <Tooltip
                  labelFormatter={(l) => fmtFecha(String(l))}
                  formatter={(v: number | string) => [
                    `$${fmtValor(v as number)}`,
                    `${moneda}/MXN`,
                  ]}
                  contentStyle={{ fontSize: 12 }}
                />
                <Line
                  type="monotone"
                  dataKey="valor"
                  stroke="#2563eb"
                  strokeWidth={2}
                  dot={false}
                  isAnimationActive={false}
                  connectNulls
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="bg-white border rounded-lg overflow-hidden">
        <div className="px-4 py-3 border-b flex items-center justify-between">
          <h3 className="text-sm font-semibold text-slate-800">
            Datos ({tablaData.length.toLocaleString("es-MX")} filas)
          </h3>
          <button
            type="button"
            onClick={() => setOrderDesc((v) => !v)}
            className="text-xs border rounded px-2 py-1 hover:bg-slate-50"
          >
            Orden: {orderDesc ? "↓ más reciente" : "↑ más antiguo"}
          </button>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-slate-100 text-slate-600 text-left">
            <tr>
              <th className="px-3 py-2 font-medium">Fecha</th>
              <th className="px-3 py-2 font-medium">Moneda</th>
              <th className="px-3 py-2 font-medium">Serie</th>
              <th className="px-3 py-2 font-medium text-right">Valor (MXN)</th>
            </tr>
          </thead>
          <tbody>
            {tablaPagina.length === 0 && (
              <tr>
                <td colSpan={4} className="px-3 py-8 text-center text-slate-500">
                  Sin datos
                </td>
              </tr>
            )}
            {tablaPagina.map((r) => (
              <tr key={`${r.fecha}-${r.serie}`} className="border-t hover:bg-slate-50">
                <td className="px-3 py-1.5">{fmtFecha(r.fecha)}</td>
                <td className="px-3 py-1.5 font-mono text-xs">{r.moneda}</td>
                <td className="px-3 py-1.5 font-mono text-xs">{r.serie}</td>
                <td className="px-3 py-1.5 text-right tabular-nums font-medium">
                  {fmtValor(r.valor)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {totalPages > 1 && (
          <div className="px-4 py-3 border-t flex items-center justify-between text-sm">
            <div className="text-slate-500">
              Página {page + 1} de {totalPages}
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                className="border rounded px-3 py-1 disabled:opacity-50"
                onClick={() => setPage((p) => Math.max(0, p - 1))}
                disabled={page === 0}
              >
                ← Anterior
              </button>
              <button
                type="button"
                className="border rounded px-3 py-1 disabled:opacity-50"
                onClick={() => setPage((p) => p + 1)}
                disabled={page + 1 >= totalPages}
              >
                Siguiente →
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-slate-600">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}
